import React, { FC, useEffect } from 'react';
import { Redirect, useParams } from 'react-router';
import { Grid } from '@material-ui/core';
import apiRoutes from '../../../../config/api/routes';
import { getSeriesMapper } from '../../../../config/api/models';
import { ExerciseSeries } from '../../../../config/api/models/Exercise';
import { GroupStudentSeriesScores, SeriesScores } from '../../../../config/api/models/Score';
import { Message, Loading } from '../../../shared/components';
import { useFetch, useUser } from '../../../shared/hooks';
import StudentSeriesItem from './StudentSeriesItem';
import TeacherSeriesItem from './TeacherSeriesItem';
import AuthorSeriesItem from './AuthorSeriesItem';
import { UrlParams } from '../../../shared/types';
import { useAppDispatch } from 'src/modules/shared/hooks/useAppDispatch';
import { setHideFilterButtons } from 'src/modules/shared/slices/filters/entity/FiltersSlice';

type Props = {
  type: 'exercises';
  studentProgressAndScores?: Record<string, SeriesScores>;
  teacherProgressAndScores?: GroupStudentSeriesScores;
};

const TrajectoryExercisesEvaluationSeriesListView: FC<Props> = ({
  type,
  studentProgressAndScores,
  teacherProgressAndScores,
}) => {
  const dispatch = useAppDispatch();
  const { trajectoryId } = useParams<UrlParams>();
  const { isAuthor, isTeacher, isStudent } = useUser();
  const [seriesResponse, getSeries] = useFetch('', {
    mapper: getSeriesMapper,
  });

  useEffect(() => {
    dispatch(setHideFilterButtons(true));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setHideFilterButtons(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!trajectoryId) return;
    getSeries({ path: apiRoutes.pages(trajectoryId, type) });
  }, [getSeries, trajectoryId, type]);

  if (!trajectoryId) return <Redirect to="/" />;

  return (
    <>
      {seriesResponse.pending && <Loading />}
      {seriesResponse.error && <Message type="error">{seriesResponse.reason}</Message>}
      {seriesResponse.success && (
        <Grid container spacing={2} alignItems="stretch">
          {seriesResponse.data.map((series: ExerciseSeries) => (
            <React.Fragment key={series.id}>
              {(isStudent || (isTeacher && !!studentProgressAndScores)) && (
                <StudentSeriesItem
                  series={series}
                  studentProgressAndScores={studentProgressAndScores}
                />
              )}
              {isTeacher && studentProgressAndScores === undefined && (
                <TeacherSeriesItem
                  series={series}
                  teacherProgressAndScores={teacherProgressAndScores}
                />
              )}
              {isAuthor && <AuthorSeriesItem series={series} />}
            </React.Fragment>
          ))}
        </Grid>
      )}
    </>
  );
};

export default TrajectoryExercisesEvaluationSeriesListView;
